import { useEffect, useState, useCallback } from "react";
import { User, ChevronDown, Check, MapPin, Lightbulb, AtSign, Loader2, CheckCircle, XCircle } from "lucide-react";
import { OnboardingData } from "@/hooks/useOnboarding";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useDebounce } from "@/hooks/useDebounce";
import { GeoCountry, GeoState, GeoCity, useGeoData } from "@/hooks/useGeoData";

interface OnboardingProfileInfoProps {
  data: OnboardingData;
  onUpdateData: (updates: Partial<OnboardingData>) => void;
  onUpdateUsername: (username: string) => void;
  onCheckUsernameAvailability: (username: string) => Promise<boolean>;
  usernameError: string;
  checkingUsername: boolean;
  countries: GeoCountry[];
  states: GeoState[];
  cities: GeoCity[];
  onSelectCountry: (code: string, name: string) => void;
  onSelectState: (id: string, name: string) => void;
  onSelectCity: (id: string, name: string) => void;
  loadingCountries: boolean;
  loadingStates: boolean;
  loadingCities: boolean;
  onFetchCountries: () => void;
  requiresState: (countryCode: string) => boolean;
  getCountryFlag: (iso2: string) => string;
}

const GENDERS = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "non_binary", label: "Non-binary" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
] as const;

const BIO_PROMPTS = [
  "What's something you're passionate about?",
  "Describe your ideal weekend",
  "What makes you laugh?",
  "What are you looking for?",
  "Share a fun fact about yourself",
];

const BIO_MAX_LENGTH = 300;

// Profession options - sorted alphabetically
const PROFESSIONS = [
  "Agriculture / Farming",
  "Artist / Designer",
  "Aviation / Maritime",
  "Business Owner",
  "Construction / Contractor",
  "Customer Service",
  "Education / Teaching",
  "Electrician",
  "Engineering",
  "Finance / Banking / Accounting",
  "Freelancer",
  "Government / Public Service",
  "Healthcare / Medical",
  "Homemaker",
  "Hospitality / Hotel / Restaurant",
  "IT / Software / Technology",
  "Legal / Lawyer",
  "Mechanic / Technician",
  "Military / Security",
  "Musician / Performer",
  "Photographer / Videographer",
  "Plumber",
  "Prefer not to say",
  "Real Estate",
  "Religious / Clergy",
  "Retail / Shopkeeping",
  "Retired",
  "Sales / Marketing",
  "Self-employed / Entrepreneur",
  "Social Media / Influencer",
  "Student",
  "Tailor / Hairdresser / Beauty",
  "Transportation / Driver / Logistics",
  "Unemployed",
  "Writer / Content Creator",
].sort(); // Ensure alphabetical order

const OnboardingProfileInfo = ({
  data,
  onUpdateData,
  onUpdateUsername,
  onCheckUsernameAvailability,
  usernameError,
  checkingUsername,
  countries,
  states,
  cities,
  onSelectCountry,
  onSelectState,
  onSelectCity,
  loadingCountries,
  loadingStates,
  loadingCities,
  onFetchCountries,
  requiresState,
  getCountryFlag,
}: OnboardingProfileInfoProps) => {
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [showStatePicker, setShowStatePicker] = useState(false);
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [showProfessionPicker, setShowProfessionPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [professionSearch, setProfessionSearch] = useState("");
  const [showBioPrompts, setShowBioPrompts] = useState(false);
  
  const debouncedUsername = useDebounce(data.username, 500);
  
  // Check username availability when debounced value changes
  useEffect(() => {
    if (debouncedUsername && debouncedUsername.length >= 3) {
      onCheckUsernameAvailability(debouncedUsername);
    }
  }, [debouncedUsername, onCheckUsernameAvailability]);

  // Fetch countries when dialog opens
  useEffect(() => {
    if (showCountryPicker && countries.length === 0) {
      onFetchCountries();
    }
  }, [showCountryPicker, countries.length, onFetchCountries]);

  const filteredCountries = countries.filter((c) =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.iso2.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const filteredStates = states.filter((s) =>
    s.name.toLowerCase().includes(stateSearch.toLowerCase()) ||
    (s.code && s.code.toLowerCase().includes(stateSearch.toLowerCase()))
  );

  const filteredCities = cities.filter((c) =>
    c.name.toLowerCase().includes(citySearch.toLowerCase())
  );

  // Get state/province label based on country
  const stateLabel = data.countryCode === "CA" ? "Province" : "State";

  // Filter professions based on search
  const filteredProfessions = PROFESSIONS.filter((profession) =>
    profession.toLowerCase().includes(professionSearch.toLowerCase())
  );

  const handlePromptClick = (prompt: string) => {
    const currentBio = data.bio;
    const separator = currentBio.trim() ? "\n\n" : "";
    onUpdateData({ bio: currentBio + separator + prompt + " " });
    setShowBioPrompts(false);
  };

  const bioLength = data.bio.length;
  const bioRemaining = BIO_MAX_LENGTH - bioLength;

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Profile Info</h3>

      {/* Display Name */}
      <div className="space-y-2">
        <label htmlFor="display-name" className="text-sm font-medium">
          Display Name <span className="text-destructive">*</span>
        </label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            id="display-name"
            name="displayName"
            type="text"
            value={data.displayName}
            onChange={(e) => onUpdateData({ displayName: e.target.value })}
            placeholder="Your name"
            maxLength={50}
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none"
          />
        </div>
        {data.displayName.trim().length > 0 && data.displayName.trim().length < 2 && (
          <p className="text-xs text-destructive">Name must be at least 2 characters</p>
        )}
        {data.displayName.trim().length === 0 && (
          <p className="text-xs text-destructive">Name is required (at least 2 characters)</p>
        )}
      </div>

      {/* Username */}
      <div className="space-y-2">
        <label htmlFor="username" className="text-sm font-medium">
          Username <span className="text-destructive">*</span>
        </label>
        <div className="relative">
          <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            id="username"
            name="username"
            type="text"
            value={data.username}
            onChange={(e) => onUpdateUsername(e.target.value)}
            placeholder="yourname"
            maxLength={20}
            className="w-full pl-10 pr-10 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none lowercase"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {checkingUsername && (
              <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
            )}
            {!checkingUsername && data.username.length >= 3 && !usernameError && (
              <CheckCircle className="w-4 h-4 text-green-500" />
            )}
            {!checkingUsername && usernameError && (
              <XCircle className="w-4 h-4 text-destructive" />
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          3-20 characters. Letters, numbers, underscores only.
        </p>
        {usernameError && (
          <p className="text-xs text-destructive">{usernameError}</p>
        )}
        {!usernameError && data.username.trim().length > 0 && data.username.trim().length < 3 && (
          <p className="text-xs text-destructive">Username must be at least 3 characters</p>
        )}
        {!usernameError && data.username.trim().length === 0 && (
          <p className="text-xs text-destructive">Username is required (at least 3 characters)</p>
        )}
      </div>

      {/* Bio */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">
            About You <span className="text-muted-foreground text-xs font-normal">(optional)</span>
          </label>
          <button
            type="button"
            onClick={() => setShowBioPrompts(!showBioPrompts)}
            className="flex items-center gap-1 text-xs text-pop hover:text-pop/80 transition-colors"
          >
            <Lightbulb className="w-3.5 h-3.5" />
            Need ideas?
          </button>
        </div>

        {/* Prompts dropdown */}
        {showBioPrompts && (
          <div className="bg-muted rounded-xl p-3 space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
            <p className="text-xs text-muted-foreground mb-2">Tap a prompt to add it:</p>
            <div className="flex flex-wrap gap-2">
              {BIO_PROMPTS.map((prompt, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => handlePromptClick(prompt)}
                  className="text-xs px-3 py-1.5 rounded-full bg-background hover:bg-pop/10 hover:text-pop transition-colors border border-border"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="relative">
          <textarea
            id="bio"
            name="bio"
            value={data.bio}
            onChange={(e) => {
              if (e.target.value.length <= BIO_MAX_LENGTH) {
                onUpdateData({ bio: e.target.value });
              }
            }}
            placeholder="Tell people a bit about yourself..."
            rows={4}
            className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none resize-none"
          />
          <span
            className={`absolute bottom-2 right-3 text-xs ${
              bioRemaining < 50 ? "text-destructive" : "text-muted-foreground"
            }`}
          >
            {bioRemaining}
          </span>
        </div>
      </div>

      {/* Gender */}
      <div className="space-y-2">
        <label className="text-sm font-medium">
          Gender <span className="text-destructive">*</span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {GENDERS.map((gender) => (
            <button
              key={gender.value}
              type="button"
              onClick={() => onUpdateData({ gender: gender.value })}
              className={`py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                data.gender === gender.value
                  ? "bg-pop text-pop-foreground"
                  : "bg-muted hover:bg-muted/80"
              }`}
            >
              {gender.label}
            </button>
          ))}
        </div>
        {!data.gender && (
          <p className="text-xs text-destructive">Gender is required</p>
        )}
      </div>

      {/* Country */}
      <div className="space-y-2">
        <label className="text-sm font-medium">
          Country <span className="text-destructive">*</span>
        </label>
        <button
          type="button"
          onClick={() => setShowCountryPicker(true)}
          className="w-full flex items-center justify-between py-3 px-4 rounded-xl bg-muted hover:bg-muted/80 transition-colors"
        >
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-muted-foreground" />
            {data.countryCode ? (
              <span>
                {getCountryFlag(data.countryCode)} {data.countryName}
              </span>
            ) : (
              <span className="text-muted-foreground">Select country</span>
            )}
          </div>
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        </button>
        {!data.countryCode && (
          <p className="text-xs text-destructive">Country is required</p>
        )}
      </div>

      {/* State/Province (for US, CA, etc.) */}
      {requiresState(data.countryCode) && data.countryCode && (
        <div className="space-y-2">
          <label className="text-sm font-medium">
            {stateLabel} <span className="text-destructive">*</span>
          </label>
          <button
            type="button"
            onClick={() => setShowStatePicker(true)}
            className="w-full flex items-center justify-between py-3 px-4 rounded-xl bg-muted hover:bg-muted/80 transition-colors"
          >
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-muted-foreground" />
              {data.stateId ? (
                <span>{data.stateName}</span>
              ) : (
                <span className="text-muted-foreground">Select {stateLabel.toLowerCase()}</span>
              )}
            </div>
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          </button>
          {!data.stateId && (
            <p className="text-xs text-destructive">{stateLabel} is required</p>
          )}
        </div>
      )}

      {/* City */}
      <div className="space-y-2">
        <label className="text-sm font-medium">
          City <span className="text-destructive">*</span>
        </label>
        <button
          type="button"
          onClick={() => {
            // For countries with states, require state first
            if (requiresState(data.countryCode) && !data.stateId) return;
            if (data.countryCode) setShowCityPicker(true);
          }}
          disabled={!data.countryCode || (requiresState(data.countryCode) && !data.stateId)}
          className="w-full flex items-center justify-between py-3 px-4 rounded-xl bg-muted hover:bg-muted/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-muted-foreground" />
            {data.cityId ? (
              <span>{data.cityName}</span>
            ) : (
              <span className="text-muted-foreground">
                {!data.countryCode 
                  ? "Select country first" 
                  : requiresState(data.countryCode) && !data.stateId
                    ? `Select ${stateLabel.toLowerCase()} first`
                    : "Select city"}
              </span>
            )}
          </div>
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        </button>
        {!data.cityId && data.countryCode && (!requiresState(data.countryCode) || data.stateId) && (
          <p className="text-xs text-destructive">City is required</p>
        )}
      </div>

      {/* Personal Information Section */}
      <div className="pt-4 border-t border-border space-y-4">
        <h3 className="text-lg font-semibold">Personal Information</h3>
        
        <div>
          <label htmlFor="marital-status" className="text-sm font-medium">Marital Status</label>
          <select
            id="marital-status"
            name="maritalStatus"
            value={data.maritalStatus}
            onChange={(e) => onUpdateData({ maritalStatus: e.target.value })}
            className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none mt-1"
          >
            <option value="">Select marital status (optional)</option>
            <option value="single">Single</option>
            <option value="divorced">Divorced</option>
            <option value="widowed">Widowed</option>
            <option value="separated">Separated</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </select>
        </div>
        
        <div>
          <label htmlFor="religion" className="text-sm font-medium">Religion</label>
          <input
            id="religion"
            name="religion"
            type="text"
            value={data.religion}
            onChange={(e) => onUpdateData({ religion: e.target.value })}
            placeholder="Your religion (optional)"
            className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none mt-1"
          />
        </div>
        
        <div>
          <label htmlFor="education" className="text-sm font-medium">Education</label>
          <input
            id="education"
            name="education"
            type="text"
            value={data.education}
            onChange={(e) => onUpdateData({ education: e.target.value })}
            placeholder="Your education level"
            className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none mt-1"
          />
        </div>
        
        <div>
          <label htmlFor="profession" className="text-sm font-medium">Profession</label>
          <button
            type="button"
            onClick={() => setShowProfessionPicker(true)}
            className="w-full flex items-center justify-between py-3 px-4 rounded-xl bg-muted hover:bg-muted/80 transition-colors mt-1 text-left"
          >
            <span className={data.profession ? "" : "text-muted-foreground"}>
              {data.profession || "Select profession (optional)"}
            </span>
            <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />
          </button>
        </div>
        
        <div>
          <label htmlFor="annual-income" className="text-sm font-medium">Annual Income</label>
          <select
            id="annual-income"
            name="annualIncome"
            value={data.annualIncome}
            onChange={(e) => onUpdateData({ annualIncome: e.target.value })}
            className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none mt-1"
          >
            <option value="">Select income range (optional)</option>
            <option value="<$25k">Less than $25,000</option>
            <option value="$25-50k">$25,000 - $50,000</option>
            <option value="$50-75k">$50,000 - $75,000</option>
            <option value="$75-100k">$75,000 - $100,000</option>
            <option value="$100-150k">$100,000 - $150,000</option>
            <option value="$150-250k">$150,000 - $250,000</option>
            <option value="$250k+">$250,000+</option>
          </select>
        </div>
        
        <div>
          <label htmlFor="socioeconomic-status" className="text-sm font-medium">Socioeconomic Status</label>
          <select
            id="socioeconomic-status"
            name="socioeconomicStatus"
            value={data.socioeconomicStatus}
            onChange={(e) => onUpdateData({ socioeconomicStatus: e.target.value })}
            className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none mt-1"
          >
            <option value="">Select socioeconomic status (optional)</option>
            <option value="working_class">Working class / Lower income</option>
            <option value="lower_middle_class">Lower-middle class</option>
            <option value="middle_class">Middle class</option>
            <option value="upper_middle_class">Upper-middle class</option>
            <option value="upper_class">Upper class / Wealthy</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </select>
        </div>
      </div>
      
      {/* Lifestyle & Intent Section */}
      <div className="pt-4 border-t border-border space-y-4">
        <h3 className="text-lg font-semibold">Lifestyle & Intent</h3>
        
        <div>
          <label htmlFor="looking-for" className="text-sm font-medium">Looking for</label>
          <select
            id="looking-for"
            name="lookingFor"
            value={data.lookingFor}
            onChange={(e) => onUpdateData({ lookingFor: e.target.value })}
            className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none mt-1"
          >
            <option value="">What are you looking for? (optional)</option>
            <option value="marriage">Marriage</option>
            <option value="serious_relationship">Serious relationship</option>
            <option value="friendship">Friendship</option>
          </select>
        </div>
        
        <div>
          <label htmlFor="willing-to-relocate" className="text-sm font-medium">Willing to relocate</label>
          <select
            id="willing-to-relocate"
            name="willingToRelocate"
            value={data.willingToRelocate}
            onChange={(e) => onUpdateData({ willingToRelocate: e.target.value })}
            className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none mt-1"
          >
            <option value="">Are you willing to relocate? (optional)</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
            <option value="maybe">Maybe</option>
          </select>
        </div>
        
        <div>
          <label htmlFor="children" className="text-sm font-medium">Children</label>
          <select
            id="children"
            name="children"
            value={data.children}
            onChange={(e) => onUpdateData({ children: e.target.value })}
            className="w-full px-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none mt-1"
          >
            <option value="">Children preference (optional)</option>
            <option value="have_children">Have children</option>
            <option value="want_children">Want children</option>
            <option value="do_not_want">Do not want</option>
          </select>
        </div>
      </div>

      {/* Age Confirmation */}
      <div className="pt-4 border-t border-border space-y-2">
        <div className="flex items-start gap-3">
          <Checkbox
            id="age-confirm"
            checked={data.ageConfirmed}
            onCheckedChange={(checked) =>
              onUpdateData({ ageConfirmed: checked === true })
            }
            className="mt-1"
          />
          <label htmlFor="age-confirm" className="text-sm cursor-pointer">
            I confirm that I am <strong>18 years or older</strong> and agree to the
            Terms of Service and Privacy Policy. <span className="text-destructive">*</span>
          </label>
        </div>
        {!data.ageConfirmed && (
          <p className="text-xs text-destructive ml-7">Age confirmation is required</p>
        )}
      </div>

      {/* Country Picker Dialog */}
      <Dialog open={showCountryPicker} onOpenChange={setShowCountryPicker}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Select Country</DialogTitle>
          </DialogHeader>
          <Input
            id="country-search"
            name="countrySearch"
            placeholder="Search countries..."
            value={countrySearch}
            onChange={(e) => setCountrySearch(e.target.value)}
          />
          <ScrollArea className="h-[300px]">
            <div className="space-y-1">
              {loadingCountries ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 rounded-full border-2 border-pop border-t-transparent animate-spin" />
                </div>
              ) : (
                filteredCountries.map((country) => (
                  <button
                    key={country.iso2}
                    type="button"
                    onClick={() => {
                      onSelectCountry(country.iso2, country.name);
                      setShowCountryPicker(false);
                      setCountrySearch("");
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                      data.countryCode === country.iso2
                        ? "bg-pop/10 text-pop"
                        : "hover:bg-muted"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{getCountryFlag(country.iso2)}</span>
                      <span className="font-medium">{country.name}</span>
                    </div>
                    {data.countryCode === country.iso2 && (
                      <Check className="w-4 h-4" />
                    )}
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* State/Province Picker Dialog */}
      <Dialog open={showStatePicker} onOpenChange={setShowStatePicker}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Select {stateLabel}</DialogTitle>
          </DialogHeader>
          <Input
            id="state-search"
            name="stateSearch"
            placeholder={`Search ${stateLabel.toLowerCase()}s...`}
            value={stateSearch}
            onChange={(e) => setStateSearch(e.target.value)}
          />
          <ScrollArea className="h-[300px]">
            <div className="space-y-1">
              {loadingStates ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 rounded-full border-2 border-pop border-t-transparent animate-spin" />
                </div>
              ) : filteredStates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No {stateLabel.toLowerCase()}s found
                </div>
              ) : (
                filteredStates.map((state) => (
                  <button
                    key={state.id}
                    type="button"
                    onClick={() => {
                      onSelectState(state.id, state.name);
                      setShowStatePicker(false);
                      setStateSearch("");
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                      data.stateId === state.id
                        ? "bg-pop/10 text-pop"
                        : "hover:bg-muted"
                    }`}
                  >
                    <span className="font-medium">{state.name}</span>
                    {data.stateId === state.id && <Check className="w-4 h-4" />}
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* City Picker Dialog */}
      <Dialog open={showCityPicker} onOpenChange={setShowCityPicker}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Select City</DialogTitle>
          </DialogHeader>
          <Input
            id="city-search"
            name="citySearch"
            placeholder="Search cities..."
            value={citySearch}
            onChange={(e) => setCitySearch(e.target.value)}
          />
          <ScrollArea className="h-[300px]">
            <div className="space-y-1">
              {loadingCities ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 rounded-full border-2 border-pop border-t-transparent animate-spin" />
                </div>
              ) : filteredCities.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No cities found</p>
                  <p className="text-xs mt-1">Try a different search term</p>
                </div>
              ) : (
                filteredCities.map((city) => (
                  <button
                    key={city.id}
                    type="button"
                    onClick={() => {
                      onSelectCity(city.id, city.name);
                      setShowCityPicker(false);
                      setCitySearch("");
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                      data.cityId === city.id
                        ? "bg-pop/10 text-pop"
                        : "hover:bg-muted"
                    }`}
                  >
                    <span className="font-medium">{city.name}</span>
                    {data.cityId === city.id && <Check className="w-4 h-4" />}
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Profession Picker Dialog */}
      <Dialog open={showProfessionPicker} onOpenChange={setShowProfessionPicker}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Select Profession</DialogTitle>
          </DialogHeader>
          <Input
            id="profession-search"
            name="professionSearch"
            placeholder="Search professions..."
            value={professionSearch}
            onChange={(e) => setProfessionSearch(e.target.value)}
          />
          <ScrollArea className="h-[300px]">
            <div className="space-y-1">
              {filteredProfessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No professions found</p>
                  <p className="text-xs mt-1">Try a different search term</p>
                </div>
              ) : (
                filteredProfessions.map((profession) => (
                  <button
                    key={profession}
                    type="button"
                    onClick={() => {
                      onUpdateData({ profession });
                      setShowProfessionPicker(false);
                      setProfessionSearch("");
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                      data.profession === profession
                        ? "bg-pop/10 text-pop"
                        : "hover:bg-muted"
                    }`}
                  >
                    <span className="font-medium">{profession}</span>
                    {data.profession === profession && <Check className="w-4 h-4" />}
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingProfileInfo;
